import {inject, injectable} from 'inversify';
import {AuthService} from './auth.service';
import {SearchParams, UrlUtils} from "../utils";

const DEFAULT_REQUEST_TIMEOUT = 60 * 1000;

export type BodyParams = Record<string, any>;

export type HttpOptions = {
    headers?: Record<string, string>;
    params?: SearchParams;
    body?: BodyParams;
    timeout?: number;
};

export class ApiError {
    constructor(public errorCode: number, public string) {
    }
}

export class HttpError {
    constructor(public status: number) {
    }
}

export class NetworkError {
    constructor(public error: any) {
    }
}

const HTTP_STATUS = {
    OK: 200,
    CREATED: 201,
    UNPROCESSABLE_ENTITY: 422,
};

const CONTENT_TYPES = {
    JSON: 'application/json',
};

@injectable()
export class HttpClientService {
    @inject(AuthService)
    private authService: AuthService;

    public get(path, params?: SearchParams, options?: HttpOptions): Promise<any> {
        options = options || {};
        options.params = params;
        return this.request('GET', path, options);
    }

    public post(path, body?: BodyParams, options?: HttpOptions): Promise<any> {
        options = options || {};
        options.body = body;
        return this.request('POST', path, options);
    }

    public put(path, body?: BodyParams, options?: HttpOptions): Promise<any> {
        options = options || {};
        options.body = body;
        return this.request('PUT', path, options);
    }

    private async request(method: 'POST' | 'GET' | 'PUT' | 'DELETE', path: string, options: HttpOptions): Promise<any> {
        const token = await this.authService.getUserIdToken();
        const controller = new AbortController();
        const signal = controller.signal;
        setTimeout(() => controller.abort(), options.timeout || DEFAULT_REQUEST_TIMEOUT);

        try {
            const resp = await fetch(UrlUtils.generateAbsoluteUrl(path, options.params), {
                method,
                body: options.body ? JSON.stringify(options.body) : null,
                headers: {
                    ...{
                        Authorization: `Bearer ${token}`,
                        'Content-Type': 'application/json',
                    },
                    ...(options.headers || {}),
                },
                signal,
            });

            switch (resp.status) {
                case HTTP_STATUS.UNPROCESSABLE_ENTITY:
                    const body = await resp.json();
                    return Promise.reject(new ApiError(body.errorCode, body.errorMsg));
                case HTTP_STATUS.OK:
                case HTTP_STATUS.CREATED:
                    return this.resolveResponse(resp);
                default:
                    return Promise.reject(new HttpError(resp.status));
            }
        } catch (error: any) {
            return Promise.reject(new NetworkError(error));
        }
    }

    private resolveResponse(resp: Response): Promise<any> {
        const contentType = resp.headers.get('Content-Type');
        if (contentType && contentType.includes(CONTENT_TYPES.JSON)) {
            return resp.json();
        } else {
            return resp.text();
        }
    }
}
